import axios from "axios";
import { ref, computed } from "vue";
import { defineStore } from "pinia";
import eventBus from "Admin/js/Utilities/EventBus.js";
import Utilities from "Admin/js/Helpers/utilities.js";
import { useUserStore } from "./User";
import { useAsyncOperation } from "Admin/js/Composables/useAsyncOperation.js";

export const useRolesAndPermissions = defineStore("rolesAndPermissions", () => {
    const user_store = useUserStore();
    const { isLoading, manageAsyncOperation } = useAsyncOperation();

    const PERMISSIONS = {
        INVITER_ADMIN: "inviter-admin",
        WORKDAY_START: "workday-start",
        WORKDAY_STOP: "workday-stop",
        CAN_SET_EVERYONE_MANDATE: "can-set-everyone-mandate",
        CAN_SET_DOWNLINE_MANDATE: "can-set-downline-mandate",
        CAN_VIEW_EVERYONE_SCREENSHOTS: "can-view-everyone-screenshots",
        CAN_VIEW_DOWNLINE_SCREENSHOTS: "can-view-downline-screenshots",
    };

    const ROLES = {
        OWNER: "owner",
        ADMIN: "admin",
        SUPPORT_ADMIN: "support_admin",
    };

    const WORKDAYS = {
        SUNDAY: 0,
        MONDAY: 1,
        TUESDAY: 2,
        WEDNESDAY: 3,
        THURSDAY: 4,
        FRIDAY: 5,
        SATURDAY: 6,
    };

    const timezone = ref(null);
    const timezoneOptions = ref([]);
    const canStartWorkdayPermission = ref({});
    const canStopWorkdayPermission = ref({});
    const roles = ref([]);
    const isWorkdayStartPermission = ref(false);
    const isWorkdayStopPermission = ref(false);
    const inviterAdminPermission = ref({});
    const canSetEveryoneMandatePermission = ref({});
    const canSetDownlineMandatePermission = ref({});
    const canViewEveryoneScreenshotsPermission = ref({});
    const canViewDownlineScreenshotsPermission = ref({});
    const officeHours = ref([]);

    const hasWorkdayStartPermission = computed(() => {
        return user_store.isAuthorizedFor(PERMISSIONS.WORKDAY_START);
    });

    const hasWorkdayStopPermission = computed(() => {
        return user_store.isAuthorizedFor(PERMISSIONS.WORKDAY_STOP);
    });

    function excludeAgentProfileIds(ids) {
        return ids.filter((id) => !user_store.agentProfilesMap.has(id));
    }

    const getOwners = computed(() => {
        const ids =
            roles.value.find((role) => role.name === ROLES.OWNER)
                ?.user_profile_ids ?? [];

        return excludeAgentProfileIds(ids);
    });

    const getSupportAdmins = computed(() => {
        if (!user_store.supportAdmin) {
            return [];
        }

        const ids =
            roles.value.find((role) => role.name === ROLES.SUPPORT_ADMIN)
                ?.user_profile_ids ?? [];

        return excludeAgentProfileIds(ids);
    });

    const getAdmins = computed(() => {
        const admin_role = roles.value.find(
            (role) => role.name === ROLES.ADMIN,
        );

        const admin_user_profile_ids = admin_role?.user_profile_ids ?? [];

        const profilesIds = [...getSupportAdmins.value, ...admin_user_profile_ids];

        return excludeAgentProfileIds([...new Set(profilesIds)]);
    });

    const getInviteAssociateProfiles = computed(() => {
        let ids = [];
        const excludeIds = new Map();

        roles.value.forEach((role) => {
            if (role.name === ROLES.SUPPORT_ADMIN && !user_store.supportAdmin) {
                role.user_profiles?.forEach((profile) => {
                    if (profile?.role === ROLES.SUPPORT_ADMIN) {
                        excludeIds.set(profile.id, true);
                    }
                });
                return;
            }
            ids.push(...role.user_profile_ids);
        });

        const allIds = [
            ...ids,
            ...(inviterAdminPermission.value.user_profile_ids ?? []),
        ];

        return excludeAgentProfileIds([
            ...new Set(allIds.filter((id) => !excludeIds.has(id))),
        ]);
    });

    const getCanSetEveryoneMandateProfiles = computed(() => {
        return excludeAgentProfileIds(
            canSetEveryoneMandatePermission.value.user_profile_ids ?? [],
        );
    });

    const getCanSetDownlineMandateProfiles = computed(() => {
        return excludeAgentProfileIds(
            canSetDownlineMandatePermission.value.user_profile_ids ?? [],
        );
    });

    const getCanViewEveryoneScreenshotsProfiles = computed(() => {
        return excludeAgentProfileIds(
            canViewEveryoneScreenshotsPermission.value.user_profile_ids ?? [],
        );
    });

    const getCanViewDownlineScreenshotsProfiles = computed(() => {
        return excludeAgentProfileIds(
            canViewDownlineScreenshotsPermission.value.user_profile_ids ?? [],
        );
    });

    function getMandatePermission(permissionKey) {
        const permissionsByKey = {
            [PERMISSIONS.CAN_SET_EVERYONE_MANDATE]:
                canSetEveryoneMandatePermission.value,
            [PERMISSIONS.CAN_SET_DOWNLINE_MANDATE]:
                canSetDownlineMandatePermission.value,
            [PERMISSIONS.CAN_VIEW_EVERYONE_SCREENSHOTS]:
                canViewEveryoneScreenshotsPermission.value,
            [PERMISSIONS.CAN_VIEW_DOWNLINE_SCREENSHOTS]:
                canViewDownlineScreenshotsPermission.value,
        };

        return permissionsByKey[permissionKey] ?? {};
    }

    function setTimezone(tz) {
        timezone.value = tz;
    }

    function getRoleByName(roleName) {
        return roles.value.find((role) => role.name === roleName);
    }

    async function fetchTimezoneOptions() {
        return manageAsyncOperation(async () => {
            const response = await axios.get("/timezones");
            const data = response.data.data ?? {};
            const keys = Object.keys(data);
            timezoneOptions.value = keys.map((key) => ({
                zone: key,
                data: data[key],
            }));
        });
    }

    async function fetchOrgTimezone() {
        return manageAsyncOperation(async () => {
            const response = await axios.get("/organizations/timezone");
            timezone.value = response.data?.data?.timezone;
        });
    }

    async function updateOrgTimezone(payload) {
        return manageAsyncOperation(async () => {
            await axios.post("/organizations/timezone", payload);
            timezone.value = payload.timezone;
        });
    }

    let permissionsRequestId = 0;
    async function fetchPermissions() {
        const requestId = ++permissionsRequestId;
        return manageAsyncOperation(async () => {
            // Ignore this response if a newer request has already started
            if (requestId !== permissionsRequestId) {
                return;
            }
            
            const { data } = await axios.get("/permissions");
            const permissions = data?.data ?? [];

            canStartWorkdayPermission.value = getPermission(
                permissions,
                PERMISSIONS.WORKDAY_START,
            );
            canStopWorkdayPermission.value = getPermission(
                permissions,
                PERMISSIONS.WORKDAY_STOP,
            );
            inviterAdminPermission.value = getPermission(
                permissions,
                PERMISSIONS.INVITER_ADMIN,
            );
            canSetEveryoneMandatePermission.value = getPermission(
                permissions,
                PERMISSIONS.CAN_SET_EVERYONE_MANDATE,
            );
            canSetDownlineMandatePermission.value = getPermission(
                permissions,
                PERMISSIONS.CAN_SET_DOWNLINE_MANDATE,
            );
            canViewEveryoneScreenshotsPermission.value = getPermission(
                permissions,
                PERMISSIONS.CAN_VIEW_EVERYONE_SCREENSHOTS,
            );
            canViewDownlineScreenshotsPermission.value = getPermission(
                permissions,
                PERMISSIONS.CAN_VIEW_DOWNLINE_SCREENSHOTS,
            );
        });
    }

    function applyMandatePermission(permission) {
        if (!permission?.name) {
            return;
        }

        if (
            matchesPermissionName(
                permission.name,
                PERMISSIONS.CAN_SET_EVERYONE_MANDATE,
            )
        ) {
            canSetEveryoneMandatePermission.value = permission;
            return;
        }

        if (
            matchesPermissionName(
                permission.name,
                PERMISSIONS.CAN_SET_DOWNLINE_MANDATE,
            )
        ) {
            canSetDownlineMandatePermission.value = permission;
            return;
        }

        if (
            matchesPermissionName(
                permission.name,
                PERMISSIONS.CAN_VIEW_EVERYONE_SCREENSHOTS,
            )
        ) {
            canViewEveryoneScreenshotsPermission.value = permission;
            return;
        }

        if (
            matchesPermissionName(
                permission.name,
                PERMISSIONS.CAN_VIEW_DOWNLINE_SCREENSHOTS,
            )
        ) {
            canViewDownlineScreenshotsPermission.value = permission;
        }
    }

    function matchesPermissionName(name, role) {
        return name === role || name.startsWith(`${role}.`);
    }

    function getPermission(data, role) {
        return (
            data.find((permission) =>
                matchesPermissionName(permission.name, role),
            ) ?? {}
        );
    }

    async function fetchRoles() {
        return manageAsyncOperation(async () => {
            const { data } = await axios.get("/roles");
            roles.value = data.data;
        });
    }

    async function updateUserPemissions(userIds, permissionId, endpoint = null) {
        return manageAsyncOperation(async () => {
            const url =
                endpoint ?? `/permissions/${permissionId}`;
            const response = await axios.post(url, {
                user_profile_ids: userIds,
            });
            return response;
        });
    }

    async function updateUserRoles(userIds, roleId) {
        return manageAsyncOperation(async () => {
            await axios.post(`/roles/${roleId}`, {
                user_profile_ids: userIds,
            });

            Utilities().showSuccessSnackbar(
                "Organization admins updated successfully",
            );
            eventBus.$emit("get-organization-roles");
        });
    }

    async function fetchOfficeHours() {
        return manageAsyncOperation(async () => {
            const { data } = await axios.get("organizations/office-hours");
            officeHours.value = data.data;
        });
    }

    async function updateOfficeHours(payload) {
        return manageAsyncOperation(async () => {
            await axios.post("organizations/office-hours", payload);
            Utilities().showSuccessSnackbar(
                "Work week hours updated successfully",
            );
        });
    }

    async function refetchRolePermissions() {
        fetchRoles();
        fetchPermissions();
    }

    return {
        timezone,
        timezoneOptions,
        isLoading,
        canStartWorkdayPermission,
        canStopWorkdayPermission,
        isWorkdayStartPermission,
        isWorkdayStopPermission,
        roles,
        hasWorkdayStartPermission,
        hasWorkdayStopPermission,
        fetchTimezoneOptions,
        fetchOrgTimezone,
        updateOrgTimezone,
        fetchPermissions,
        fetchRoles,
        setTimezone,
        inviterAdminPermission,
        canSetEveryoneMandatePermission,
        canSetDownlineMandatePermission,
        canViewEveryoneScreenshotsPermission,
        canViewDownlineScreenshotsPermission,
        ROLES,
        PERMISSIONS,
        getOwners,
        getSupportAdmins,
        getAdmins,
        getInviteAssociateProfiles,
        getCanSetEveryoneMandateProfiles,
        getCanSetDownlineMandateProfiles,
        getCanViewEveryoneScreenshotsProfiles,
        getCanViewDownlineScreenshotsProfiles,
        getMandatePermission,
        applyMandatePermission,
        updateUserPemissions,
        updateUserRoles,
        getRoleByName,
        fetchOfficeHours,
        officeHours,
        WORKDAYS,
        updateOfficeHours,
        refetchRolePermissions,
    };
});
